import { Form, Input, Radio } from "antd";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { CustomActionButtons, CustomInput, CustomModal } from "sipli-fleet-core/components";
import {
  useMutationCreateCorporationPosition,
  useMutationUpdateCorporationPosition,
} from "src/queries/administrationSetup.queries";
import type {
  GeneralSetupPayload,
  GeneralSetupRecord,
} from "src/services/administrationSetupService";
import Notification from "src/util/libs/notification";
import { isActive } from "../setupUtils";

interface CorporationPositionAddEditProps {
  open: boolean;
  record?: GeneralSetupRecord | null;
  onClose: () => void;
}
interface CorporationPositionFormValues {
  name: string;
  code?: string;
  active: boolean;
  description?: string;
}
const CorporationPositionAddEdit = ({ open, record, onClose }: CorporationPositionAddEditProps) => {
  const { t } = useTranslation();
  const [form] = Form.useForm<CorporationPositionFormValues>();
  const createMutation = useMutationCreateCorporationPosition();
  const updateMutation = useMutationUpdateCorporationPosition();
  useEffect(() => {
    if (open)
      form.setFieldsValue({
        name: record?.name ?? "",
        code: record?.code ?? "",
        active: record ? isActive(record.active) : true,
        description: record?.description ?? "",
      });
  }, [form, open, record]);
  const handleClose = () => {
    form.resetFields();
    onClose();
  };
  const handleSubmit = async () => {
    const values = await form.validateFields();
    const payload: GeneralSetupPayload = {
      name: values.name,
      code: values.code,
      active: values.active,
      description: values.description ?? "",
    };
    try {
      if (record) await updateMutation.mutateAsync({ id: record.id, payload });
      else await createMutation.mutateAsync(payload);
      Notification.success({ message: record ? t("update_position") : t("add_position") });
      handleClose();
    } catch (error: unknown) {
      const responseMessage =
        error instanceof Error
          ? (error as { response?: { data?: { message?: string } } }).response?.data?.message
          : undefined;
      if (!responseMessage)
        Notification.error({ message: t("error"), description: t("setup_save_failed") });
    }
  };
  return (
    <CustomModal
      open={open}
      title={`${record ? t("edit") : t("add")} ${t("position")}`}
      onCancel={handleClose}
      destroyOnHidden
      width={560}
      footer={null}
    >
      <Form
        form={form}
        layout="vertical"
        requiredMark={false}
        className="grid grid-cols-1 pt-4 gap-x-4 sm:grid-cols-2"
      >
        <Form.Item name="code" label={t("code")} className="mb-3!">
          <CustomInput />
        </Form.Item>
        <Form.Item
          name="name"
          label={t("name")}
          rules={[{ required: true, message: t("name_required") }]}
          className="mb-3!"
        >
          <CustomInput />
        </Form.Item>
        <Form.Item name="active" label={t("status")} className="mb-3!">
          <Radio.Group optionType="button" buttonStyle="solid">
            <Radio.Button value>{t("active")}</Radio.Button>
            <Radio.Button value={false}>{t("inactive")}</Radio.Button>
          </Radio.Group>
        </Form.Item>
        <Form.Item name="description" label={t("description")} className="mb-3! sm:col-span-2">
          <Input.TextArea rows={4} />
        </Form.Item>
        <div className="sm:col-span-2">
          <CustomActionButtons
            onCancel={handleClose}
            onOk={handleSubmit}
            okBtnLabel={record ? "update" : "add"}
            loading={createMutation.isPending || updateMutation.isPending}
          />
        </div>
      </Form>
    </CustomModal>
  );
};
export default CorporationPositionAddEdit;
